import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { issueDisciplinaryAction, listDisciplinaryActions, listEmployees, listTardiness } from '../../api/hr';
import { usePermissions } from '../../context/PermissionContext';
import { useBranch } from '../../context/BranchContext';
import {
  Button,
  ClickableRow,
  Drawer,
  DrawerField,
  EmptyState,
  ErrorNote,
  FieldLabel,
  ListToolbar,
  LoadingNote,
  PageHeader,
  Pagination,
  Select,
  StatusBadge,
  SummaryStrip,
  TextInput,
  formatDateTime,
  statusCounts,
  tableStyle,
  tdStyle,
  thStyle,
  usePagination,
} from '../../components/common';
import type { DisciplinaryAction, Employee, Tardiness } from '../../types/domain';

const DISC_STATUSES: DisciplinaryAction['status'][] = ['ACTIVE', 'RESCINDED'];
const ACTION_TYPE_LABEL: Record<DisciplinaryAction['action_type'], string> = {
  WARNING: 'Peringatan',
  SUSPENSION: 'Skorsing',
  TERMINATION: 'Pemutusan',
  COMMENDATION: 'Penghargaan',
  OTHER: 'Lainnya',
};

/**
 * Disciplinary workspace (FASE 9, migration 0040/0041). Sensitive HR data —
 * read AND write are restricted to HR + TENANT_OWNER by both the
 * permission catalog and (migration 0041) a role-restrictive RLS policy on
 * disciplinary_actions itself, not client-side hiding alone. Branch-scoped
 * — re-fetches on Cabang Aktif change.
 *
 * STATUS: IMPLEMENTED_UNVERIFIED — see docs/data-dictionary.md NEEDS_DECISION log.
 */
export default function DisciplinaryPage() {
  const { hasPermission } = usePermissions();
  const { activeBranchId } = useBranch();
  const [actions, setActions] = useState<DisciplinaryAction[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [tardiness, setTardiness] = useState<Tardiness[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [selected, setSelected] = useState<DisciplinaryAction | null>(null);

  const canRead = hasPermission('disciplinary_action', 'read');
  const canCreate = hasPermission('disciplinary_action', 'create');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [actionRows, empRows, tardyRows] = await Promise.all([
        listDisciplinaryActions(activeBranchId ?? undefined),
        listEmployees(activeBranchId ?? undefined),
        listTardiness(activeBranchId ?? undefined),
      ]);
      setActions(actionRows);
      setEmployees(empRows);
      setTardiness(tardyRows);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canRead) void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBranchId, canRead]);

  const employeeName = (id: string) => employees.find((e) => e.employee_id === id)?.legal_name ?? id;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return actions.filter((a) => {
      if (q && !employeeName(a.employee_id).toLowerCase().includes(q)) return false;
      if (statusFilter !== 'ALL' && a.status !== statusFilter) return false;
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actions, search, statusFilter, employees]);
  const { pageRows, page, pageCount, setPage, start, pageSize, total } = usePagination(filtered, 15);

  if (!canRead) {
    return (
      <div>
        <PageHeader title="Disiplin" subtitle="Data sensitif — akses terbatas HR/Owner." />
        <EmptyState text="Anda tidak memiliki akses untuk melihat data disiplin. Ini dibatasi di server (RLS), bukan hanya di tampilan." variant="section" />
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Disiplin" subtitle="Tindakan disiplin karyawan — data sensitif, HR/Owner only. Migrasi 0040/0041." />
      {canCreate && (
        <Button variant="primary" onClick={() => setShowForm((v) => !v)} style={{ marginBottom: 16 }}>
          {showForm ? 'Batal' : '+ Terbitkan Tindakan'}
        </Button>
      )}
      {showForm && canCreate && (
        <NewDisciplinaryForm
          employees={employees}
          tardiness={tardiness}
          onCreated={() => {
            setShowForm(false);
            void load();
          }}
        />
      )}
      {loading && <LoadingNote />}
      {error && <ErrorNote text={error} />}
      {!loading && !error && (
        <>
          <SummaryStrip
            items={(() => {
              const c = statusCounts(actions, DISC_STATUSES);
              return [
                { label: 'Total', value: actions.length },
                { label: 'Aktif', value: c.ACTIVE, tone: 'danger' as const },
                { label: 'Dicabut', value: c.RESCINDED, tone: 'neutral' as const },
              ];
            })()}
          />
          <ListToolbar
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder="Cari nama karyawan..."
            resultCount={total}
            filters={
              <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ width: 160 }}>
                <option value="ALL">Semua status</option>
                <option value="ACTIVE">Aktif</option>
                <option value="RESCINDED">Dicabut</option>
              </Select>
            }
          />
          {actions.length === 0 && <EmptyState text="Belum ada tindakan disiplin." />}
          {actions.length > 0 && total === 0 && <EmptyState text="Tidak ada tindakan yang cocok dengan pencarian/filter." />}
          {total > 0 && (
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Karyawan</th>
                  <th style={thStyle}>Jenis</th>
                  <th style={thStyle}>Diterbitkan</th>
                  <th style={thStyle}>Status</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((a) => (
                  <ClickableRow key={a.action_id} onClick={() => setSelected(a)}>
                    <td style={tdStyle}>{employeeName(a.employee_id)}</td>
                    <td style={tdStyle}>{ACTION_TYPE_LABEL[a.action_type]}</td>
                    <td style={tdStyle}>{formatDateTime(a.issued_at)}</td>
                    <td style={tdStyle}>
                      <StatusBadge status={a.status} />
                    </td>
                  </ClickableRow>
                ))}
              </tbody>
            </table>
          )}
          <Pagination page={page} pageCount={pageCount} setPage={setPage} start={start} pageSize={pageSize} total={total} />
        </>
      )}
      <Drawer open={!!selected} onClose={() => setSelected(null)} title={selected ? employeeName(selected.employee_id) : 'Detail'}>
        {selected && (
          <div>
            <DrawerField label="Jenis" value={ACTION_TYPE_LABEL[selected.action_type]} />
            <DrawerField label="Status" value={<StatusBadge status={selected.status} />} />
            <DrawerField label="Alasan" value={selected.reason ?? '—'} />
            <DrawerField label="Diterbitkan" value={formatDateTime(selected.issued_at)} />
            {selected.related_tardiness_id && <DrawerField label="Terkait Keterlambatan" value="Ya" />}
          </div>
        )}
      </Drawer>
    </div>
  );
}

function NewDisciplinaryForm({
  employees,
  tardiness,
  onCreated,
}: {
  employees: Employee[];
  tardiness: Tardiness[];
  onCreated: () => void;
}) {
  const [employeeId, setEmployeeId] = useState('');
  const [actionType, setActionType] = useState<DisciplinaryAction['action_type']>('WARNING');
  const [reason, setReason] = useState('');
  const [relatedTardinessId, setRelatedTardinessId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const employeeTardiness = useMemo(() => tardiness.filter((t) => t.employee_id === employeeId), [tardiness, employeeId]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!reason.trim()) {
      setError('Alasan wajib diisi.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await issueDisciplinaryAction(employeeId, actionType, reason.trim(), relatedTardinessId || null);
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="alex-card" style={{ marginBottom: 20, padding: 16, display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
      <div style={{ flex: 1, minWidth: 180 }}>
        <FieldLabel label="Karyawan">
          <Select value={employeeId} onChange={(e) => { setEmployeeId(e.target.value); setRelatedTardinessId(''); }} required>
            <option value="" disabled>
              Pilih...
            </option>
            {employees.map((emp) => (
              <option key={emp.employee_id} value={emp.employee_id}>
                {emp.employee_no} — {emp.legal_name}
              </option>
            ))}
          </Select>
        </FieldLabel>
      </div>
      <div style={{ width: 150 }}>
        <FieldLabel label="Jenis">
          <Select value={actionType} onChange={(e) => setActionType(e.target.value as DisciplinaryAction['action_type'])}>
            <option value="WARNING">Peringatan</option>
            <option value="SUSPENSION">Skorsing</option>
            <option value="TERMINATION">Pemutusan</option>
            <option value="COMMENDATION">Penghargaan</option>
            <option value="OTHER">Lainnya</option>
          </Select>
        </FieldLabel>
      </div>
      {employeeTardiness.length > 0 && (
        <div style={{ width: 200 }}>
          <FieldLabel label="Terkait keterlambatan (opsional)">
            <Select value={relatedTardinessId} onChange={(e) => setRelatedTardinessId(e.target.value)}>
              <option value="">—</option>
              {employeeTardiness.map((t) => (
                <option key={t.tardiness_id} value={t.tardiness_id}>
                  {t.late_minutes} menit — {new Date(t.created_at).toLocaleDateString('id-ID')}
                </option>
              ))}
            </Select>
          </FieldLabel>
        </div>
      )}
      <div style={{ flex: 2, minWidth: 220 }}>
        <FieldLabel label="Alasan">
          <TextInput value={reason} onChange={(e) => setReason(e.target.value)} required />
        </FieldLabel>
      </div>
      <Button type="submit" variant="primary" loading={submitting}>
        Terbitkan
      </Button>
      {error && <ErrorNote text={error} />}
    </form>
  );
}
